/**
 * ACC (Autodesk Construction Cloud) Utility Functions
 * Helper functions for generating ACC URLs and device detection
 */

import { PROJECT_IDS } from '../config/projects'

/**
 * Detects if the current device is a mobile device
 * @returns {boolean} - True if mobile device or small screen
 */
export const isMobileDevice = () => {
  const userAgent = navigator.userAgent || window.opera
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
  const isMobileUA = mobileRegex.test(userAgent.toLowerCase())
  const isSmallScreen = window.innerWidth <= 768
  return isMobileUA || isSmallScreen
}

/**
 * Generates ACC URL based on device type and report type
 * Dynamically creates the correct ACC URL for different workflow types:
 * - Contracts/Subcontracts: /cost/cost/projects/{projectId}/contracts?preview={id}
 * - SCO Change Orders: /cost/changeorder/projects/{projectId}/changeorders/sco?preview={id}
 * - OCO Change Orders: /cost/changeorder/projects/{projectId}/changeorders/oco?preview={id}
 * - ACC Forms: /build/forms/projects/{projectId}/forms/{id}
 * - Field Reports: /build/forms/projects/{projectId}/field-reports/{templateId}/reports/{id}
 *
 * @param {Object} report - The report/form/subcontract/change order object
 * @param {string} projectId - The project ID
 * @param {string} templateId - The template ID (for field reports)
 * @returns {string} - The ACC URL
 */
export const getACCUrl = (report, projectId, templateId) => {
  const baseUrl = 'https://acc.autodesk.com'
  let formPath

  // Determine path based on report type
  if (report._isChangeOrder) {
    // Change Order URL - Cost Management change order detail view
    // Dynamically determine change order type: sco (Subcontractor) or oco (Owner)
    // Format: /cost/changeorder/projects/{projectId}/changeorders/{type}?preview={changeOrderId}
    const changeOrderProjectId = report.projectId || projectId

    // Determine change order type from the report data
    // Check multiple possible properties: _changeOrderType, _workflowType, type, changeOrderType
    // _changeOrderType will be 'SCO' or 'OCO' (set by getAllChangeOrders in changeOrderService)
    let changeOrderType = 'sco' // default to sco

    // Priority order: _changeOrderType (most specific) -> _workflowType -> type -> changeOrderType
    if (report._changeOrderType) {
      // _changeOrderType is set to 'SCO' or 'OCO' by getAllChangeOrders()
      changeOrderType = report._changeOrderType.toLowerCase()
    } else if (report._workflowType) {
      const workflowTypeStr = report._workflowType.toLowerCase()
      if (workflowTypeStr.includes('oco') || workflowTypeStr.includes('owner')) {
        changeOrderType = 'oco'
      }
    } else if (report.type) {
      const typeStr = report.type.toLowerCase()
      if (typeStr.includes('oco') || typeStr.includes('owner')) {
        changeOrderType = 'oco'
      }
    } else if (report.changeOrderType) {
      const coTypeStr = report.changeOrderType.toLowerCase()
      if (coTypeStr.includes('oco') || coTypeStr.includes('owner')) {
        changeOrderType = 'oco'
      }
    }

    formPath = `/cost/changeorder/projects/${changeOrderProjectId}/changeorders/${changeOrderType}?preview=${report.id}`
  } else if (report._isSubcontract || report._isContract) {
    // Contract URL (including PO and Subcontract) - Cost Management contract detail view
    // Format: /cost/cost/projects/{projectId}/contracts?preview={contractId}
    const contractProjectId = report.projectId || projectId
    formPath = `/cost/cost/projects/${contractProjectId}/contracts?preview=${report.id}`
  } else if (report._isForm || projectId === PROJECT_IDS.HOCON_SANDBOX) {
    // ACC Form URL
    const formProjectId = report.projectId || projectId
    formPath = `/build/forms/projects/${formProjectId}/forms/${report.id}`
  } else {
    // Field Report URL (default)
    const reportProjectId = report.projectId || projectId
    const reportTemplateId = templateId
    formPath = `/build/forms/projects/${reportProjectId}/field-reports/${reportTemplateId}/reports/${report.id}`
  }

  if (isMobileDevice()) {
    return `${baseUrl}${formPath}?view=mobile`
  } else {
    return `${baseUrl}${formPath}`
  }
}
